/**
 * File: assets/frequently-bought-together.js
 *
 * Custom element per la sezione "Spesso acquistati assieme".
 * Essendo un web component, si inizializza da solo ogni volta che finisce
 * nel DOM: al caricamento pagina, dopo un salvataggio nell'editor del tema,
 * o dopo un'iniezione via Section Rendering API.
 */

class FrequentlyBoughtTogether extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-ready')) return;
    this.setAttribute('data-ready', '');

    // Contenuto gia' presente (metafield renderizzato lato server, oppure
    // risposta dell'endpoint): serve solo agganciare gli eventi.
    if (this.hasAttribute('data-server-rendered') || this.querySelector('[data-fbt-add]')) {
      this.bind();
    } else {
      this.load();
    }
  }

  get endpoint() {
    const params = new URLSearchParams({
      section_id: this.dataset.sectionId,
      product_id: this.dataset.productId,
      limit: this.dataset.limit,
      intent: this.dataset.intent
    });

    // Serve solo per l'anteprima di un tema non pubblicato fuori dall'editor.
    // Dentro l'editor va omesso, altrimenti Shopify risponde con un redirect.
    const theme = window.Shopify?.theme;
    if (!window.Shopify?.designMode && theme?.id && theme?.role !== 'main') {
      params.set('preview_theme_id', theme.id);
    }

    return `${this.dataset.baseUrl}?${params.toString()}`;
  }

  async load() {
    const url = this.endpoint;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const source = doc.querySelector('frequently-bought-together');

      if (!source || !source.innerHTML.trim()) {
        this.explainEmpty('Nessun prodotto suggerito per questo articolo.');
        return;
      }

      this.innerHTML = source.innerHTML;
      this.bind();
    } catch (e) {
      console.error('[FBT] Caricamento fallito:', e.message, url);
      this.explainEmpty(`Caricamento fallito: ${e.message}`);
    }
  }

  /** In editor mostra il motivo; sul negozio resta invisibile. */
  explainEmpty(message) {
    if (!window.Shopify?.designMode) return;
    this.innerHTML = `<p class="fbt__notice">Spesso acquistati assieme — ${message}</p>`;
  }

  money(cents) {
    const fmt = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const value = cents / 100;

    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const withComma = group(value.toFixed(2).replace('.', ','), '.');
    const withPeriod = group(value.toFixed(2), ',');
    const noDecComma = group(Math.round(value).toString(), '.');
    const noDecPeriod = group(Math.round(value).toString(), ',');

    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecPeriod)
      .replace(/\{\{\s*amount\s*\}\}/g, withPeriod);
  }

  bind() {
    const button = this.querySelector('[data-fbt-add]');
    if (!button) return;

    const checks = Array.from(this.querySelectorAll('[data-fbt-check]'));
    const totalEl = this.querySelector('[data-fbt-total]');
    const errorEl = this.querySelector('[data-fbt-error]');
    const currentPrice = parseInt(button.dataset.currentPrice, 10) || 0;

    const refresh = () => {
      let total = currentPrice;
      let n = 1;

      checks.forEach((c) => {
        // Miniatura mostrata solo se l'articolo è selezionato
        const thumb = this.querySelector(`[data-thumb="${c.value}"]`);
        if (thumb) thumb.classList.toggle('is-hidden', !c.checked);

        if (c.checked) {
          total += parseInt(c.dataset.price, 10) || 0;
          n += 1;
        }
      });

      totalEl.textContent = this.money(total);
      button.textContent = FrequentlyBoughtTogether.buttonLabel(n);
    };

    checks.forEach((c) => c.addEventListener('change', refresh));

    // Cambio variante: il <select> vive nel pannello <details>, quindi
    // raggiunge la riga tramite data-target invece che con closest().
    this.querySelectorAll('[data-fbt-variant]').forEach((select) => {
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const check = this.querySelector(`#${CSS.escape(select.dataset.target)}`);
        if (!check) return;

        const previousId = check.value;
        const price = parseInt(option.dataset.price, 10) || 0;

        check.value = option.value;
        check.dataset.price = price;

        const row = check.closest('.fbt__row');
        const priceEl = row?.querySelector('[data-fbt-row-price]');
        if (priceEl) priceEl.textContent = this.money(price);

        const variantEl = row?.querySelector('[data-fbt-row-variant]');
        if (variantEl) variantEl.textContent = option.dataset.title || '';

        const thumb = this.querySelector(`[data-thumb="${previousId}"]`);
        if (thumb) {
          thumb.dataset.thumb = option.value;
          const img = thumb.querySelector('img');
          if (img && option.dataset.image) img.src = option.dataset.image;
        }

        refresh();
      });
    });

    refresh();

    button.addEventListener('click', () => this.addToCart(button, checks, errorEl));
  }

  /** "Aggiungi tutti e tre al carrello", come nel pattern Amazon. */
  static buttonLabel(count) {
    const words = {
      2: 'entrambi',
      3: 'tutti e tre',
      4: 'tutti e quattro',
      5: 'tutti e cinque',
      6: 'tutti e sei',
      7: 'tutti e sette'
    };
    if (count <= 1) return 'Aggiungi al carrello';
    return `Aggiungi ${words[count] || `tutti e ${count}`} al carrello`;
  }

  /**
   * Notifica il tema del carrello aggiornato. Le versioni di Horizon
   * differiscono: alcune esportano la classe CartUpdateEvent, altre solo
   * i nomi in ThemeEvents. Proviamo in ordine e ripieghiamo su CustomEvent.
   */
  async notifyTheme(cart) {
    const detail = {
      itemCount: cart.item_count,
      source: 'frequently-bought-together',
      sections: {}
    };

    try {
      const events = await import('@theme/events');
      const name = events.ThemeEvents?.cartUpdate || 'cart:update';

      if (typeof events.CartUpdateEvent === 'function') {
        document.dispatchEvent(new events.CartUpdateEvent(cart, 'frequently-bought-together', detail));
      } else {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { ...detail, data: cart, cart } }));
      }
    } catch (e) {
      console.warn('[FBT] Modulo @theme/events non disponibile, uso evento generico.', e);
      document.dispatchEvent(
        new CustomEvent('cart:update', { bubbles: true, detail: { ...detail, data: cart, cart } })
      );
    }

    await this.refreshDrawer();
    await this.refreshCartCount(cart);
    this.openDrawer();
  }

  /** Elemento del carrello laterale, qualunque nome abbia in questo tema. */
  findDrawer() {
    const selectors = [
      'cart-drawer-component',
      'cart-drawer',
      '#cart-drawer',
      '[id*="cart-drawer"]'
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  /**
   * Rigenera il markup del drawer con la Section Rendering API: l'evento
   * di aggiornamento da solo non ricostruisce l'HTML, quindi il drawer
   * mostrerebbe il contenuto che aveva al caricamento della pagina.
   */
  async refreshDrawer() {
    const drawer = this.findDrawer();
    if (!drawer) return;

    const wrapper = drawer.closest('.shopify-section') || drawer.parentElement?.closest('[id^="shopify-section-"]');
    const sectionId = wrapper?.id?.replace('shopify-section-', '');
    if (!sectionId) {
      console.warn('[FBT] Sezione del drawer non identificata: non posso aggiornarne il contenuto.');
      return;
    }

    try {
      const res = await fetch(`${window.Shopify.routes.root}?sections=${encodeURIComponent(sectionId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const html = data[sectionId];
      if (!html) throw new Error('Sezione assente nella risposta');

      const fresh = new DOMParser().parseFromString(html, 'text/html');

      // Sostituiamo solo l'interno del componente, cosi' non perdiamo
      // eventuali stati o listener applicati al contenitore esterno.
      const freshDrawer =
        fresh.querySelector(drawer.tagName.toLowerCase()) ||
        fresh.querySelector(`#${CSS.escape(drawer.id || '')}`);

      if (freshDrawer) {
        drawer.innerHTML = freshDrawer.innerHTML;
      } else {
        wrapper.innerHTML = fresh.querySelector(`#shopify-section-${sectionId}`)?.innerHTML || wrapper.innerHTML;
      }
    } catch (e) {
      console.warn('[FBT] Aggiornamento del drawer non riuscito.', e);
    }
  }

  /**
   * Aggiorna il contatore articoli nell'header.
   *
   * Prendiamo il badge gia' renderizzato dal server e ne sostituiamo il
   * markup: scrivere il numero a mano rischia di colpire sia il badge sia
   * uno span interno, con due cifre sovrapposte.
   */
  async refreshCartCount(cart) {
    const badgeSelector = [
      '.cart-bubble',
      '.cart-count-bubble',
      '.cart-count',
      '.header__cart-count',
      '[data-cart-count]'
    ].join(', ');

    // Solo i badge piu' esterni: scartiamo quelli annidati in un altro match.
    const all = Array.from(document.querySelectorAll(badgeSelector));
    const badges = all.filter((el) => !all.some((other) => other !== el && other.contains(el)));
    if (!badges.length) {
      console.warn('[FBT] Contatore carrello non trovato nell\'header.');
      return;
    }

    const wrapper = badges[0].closest('[id^="shopify-section-"]');
    const sectionId = wrapper?.id?.replace('shopify-section-', '');

    if (sectionId) {
      try {
        const res = await fetch(`${window.Shopify.routes.root}?sections=${encodeURIComponent(sectionId)}`);
        if (res.ok) {
          const data = await res.json();
          const fresh = new DOMParser().parseFromString(data[sectionId] || '', 'text/html');
          const freshBadges = fresh.querySelectorAll(badgeSelector);
          const freshOuter = Array.from(freshBadges).filter(
            (el) => !Array.from(freshBadges).some((other) => other !== el && other.contains(el))
          );

          if (freshOuter.length === badges.length) {
            badges.forEach((badge, i) => badge.replaceWith(freshOuter[i].cloneNode(true)));
            return;
          }
        }
      } catch (e) {
        console.warn('[FBT] Header non aggiornabile via sezione, uso il fallback.', e);
      }
    }

    // Fallback: riscriviamo solo i nodi di testo, lasciando intatti gli
    // span per screen reader e le icone.
    badges.forEach((badge) => {
      const textNodes = Array.from(badge.childNodes).filter((n) => n.nodeType === Node.TEXT_NODE);
      if (textNodes.length) {
        textNodes.forEach((n, i) => {
          n.textContent = i === 0 ? String(cart.item_count) : '';
        });
      } else {
        badge.textContent = String(cart.item_count);
      }

      if (cart.item_count > 0) {
        badge.removeAttribute('hidden');
        badge.classList.remove('hidden', 'is-empty');
        badge.setAttribute('aria-hidden', 'false');
      }
    });
  }

  /**
   * Apre il carrello laterale. Horizon lo espone in modi diversi a seconda
   * della versione, quindi proviamo in cascata prima di rassegnarci alla
   * pagina carrello.
   */
  openDrawer() {
    const candidates = [this.findDrawer(), document.querySelector('dialog[id*="cart"]')].filter(Boolean);

    for (const el of candidates) {
      // Metodi possibili, in ordine di preferenza
      for (const method of ['open', 'show', 'showDialog', 'showModal']) {
        if (typeof el[method] === 'function') {
          try {
            el[method]();
            return true;
          } catch (e) {
            /* proviamo il prossimo */
          }
        }
      }

      // Alcune build usano un <dialog> interno
      const dialog = el.querySelector('dialog');
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        return true;
      }

      // Altre si aprono con un attributo
      if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') {
        el.removeAttribute('hidden');
        el.setAttribute('aria-hidden', 'false');
        el.classList.add('is-open', 'active');
        return true;
      }
    }

    // Ultimo tentativo: simulare il clic sull'icona carrello dell'header,
    // che e' cio' che apre il drawer nel normale flusso del tema.
    const opener = document.querySelector(
      'a[href$="/cart"][aria-controls], button[aria-controls*="cart"], [data-cart-drawer-trigger], .header__cart-button'
    );
    if (opener) {
      opener.click();
      return true;
    }

    console.warn('[FBT] Drawer non trovato, apro la pagina carrello.');
    window.location.href = `${window.Shopify.routes.root}cart`;
    return false;
  }

  async addToCart(button, checks, errorEl) {
    const items = [{ id: parseInt(button.dataset.current, 10), quantity: 1 }];
    checks.forEach((c) => {
      if (c.checked) items.push({ id: parseInt(c.value, 10), quantity: 1 });
    });

    errorEl.hidden = true;
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Aggiunta in corso…';

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Impossibile aggiungere gli articoli.');
      }
    } catch (e) {
      // Solo qui l'aggiunta e' davvero fallita: lo segnaliamo.
      errorEl.textContent = `${e.message} Riprova o deseleziona un articolo non disponibile.`;
      errorEl.hidden = false;
      button.disabled = false;
      button.textContent = original;
      return;
    }

    // Aggiunta riuscita. Un problema nell'aggiornare la UI non deve piu'
    // comparire come errore all'utente: al massimo si ricarica la pagina.
    try {
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((r) => r.json());
      await this.notifyTheme(cart);
    } catch (e) {
      console.warn('[FBT] Aggiornamento UI non riuscito, ricarico.', e);
      window.location.reload();
      return;
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }
}

if (!customElements.get('frequently-bought-together')) {
  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
}